<script lang="ts" setup>
import { computed } from "vue";
import { useDateTime } from "@/composables";
import { useAppStore } from "@/store/app.store";
import type { CurrentLocationDotBG, CurrentLocationDotSettings } from "@/types/props";

const { dateTZ, timeAgo, hasTimePassed } = useDateTime();
const { dispatcher } = useAppStore();

const props = withDefaults(
  defineProps<{
    timezone?: string;
    showTooltip?: boolean;
    time: string | Date;
    dotSettings: CurrentLocationDotSettings[];
    dotClass?: string;
    hideDot?: boolean;
    prevTime?: string | Date;
  }>(),
  {
    timezone: "",
    dotClass: "",
    showTooltip: true,
    hideDot: false,
    prevTime: "",
  },
);

const allDotClasses = computed(() => {
  const classes = props.dotClass.split(" ");

  if (props.hideDot) {
    classes.push("dot--hidden");
  }

  return classes.join(" ");
});

const dotColor = computed(() => {
  let result: CurrentLocationDotBG = "success";

  for (const setting of props.dotSettings) {
    if (hasTimePassed(props.time, setting.seconds)) {
      result = setting.color;
      break;
    }
  }

  return result;
});
</script>

<template>
  <v-tooltip v-if="dispatcher" :disabled="!showTooltip" location="top start" open-on-focus>
    <template #activator="{ props: tooltipProps }">
      <div v-bind="tooltipProps">
        <div :class="[dotColor, allDotClasses]" class="dot">
          {{ dateTZ(time, dispatcher.timezone) }}
        </div>
        <div
          v-if="timezone && dispatcher.timezone !== timezone"
          class="text-grey-darken-2 font-size-11"
          data-qa="truck-location-time"
        >
          {{ dateTZ(time, timezone) }}
        </div>
      </div>
    </template>

    Location updated {{ timeAgo(time) }}

    <template v-if="prevTime">
      <br />
      Tracked after {{ timeAgo(prevTime) }}
    </template>
  </v-tooltip>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.dot {
  position: relative;

  &:before {
    content: "";
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
    position: absolute;
    left: -10px;
    top: 50%;
    transform: translateY(-50%);
  }

  &--hidden {
    &:before {
      display: none;
    }
  }

  &.grey {
    &:before {
      background-color: $grey;
    }
  }

  &.success {
    &:before {
      background-color: $green;
    }
  }

  &.yellow {
    &:before {
      background-color: $yellow;
    }
  }

  &.orange {
    &:before {
      background-color: $warning;
    }
  }
}
</style>
